/**
 * O código curto de convite de uma sala, como os DOIS lados o entendem.
 *
 * O `match_id` continua sendo a identidade da sala para o contrato e o replay
 * — 32 hex, impossível de ditar por voz. O convite é outra coisa: seis
 * caracteres que cabem numa mensagem e sobrevivem a ser lidos em voz alta.
 *
 * O alfabeto tira o que se confunde na leitura: 0/O e 1/I/l. Por isso NÃO há
 * mapeamento de O→0 nem I/L→1 ao normalizar: 0 e 1 nem existem no alfabeto,
 * então "O" e "I" só podem ser erro de digitação — e L é uma letra legítima,
 * que um mapeamento quebraria. Um código com esses caracteres falha na
 * validação com a frase certa, em vez de virar silenciosamente outro código.
 */

export const INVITE_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ'
export const INVITE_LENGTH = 6

const VALIDO = new RegExp(`^[${INVITE_ALPHABET}]{${INVITE_LENGTH}}$`)

/**
 * O que o jogador digitou, na forma canônica: maiúsculas, sem espaços nem
 * hífens (gente escreve "k7m-2px" e "K7M 2PX"). Nada além disso — ver o
 * cabeçalho sobre O/I/L.
 */
export const normalizarCodigo = (texto: string): string =>
  texto.toUpperCase().replace(/[\s-]/g, '')

/** Seis caracteres do alfabeto, exatamente. Aplique em texto já normalizado. */
export const codigoValido = (codigo: string): boolean => VALIDO.test(codigo)

/**
 * Um código novo a partir de bytes aleatórios.
 *
 * Quem chama fornece a aleatoriedade (`crypto.randomBytes` no servidor) e
 * confere a unicidade entre as salas vivas — 32^6 ≈ 1 bilhão de códigos
 * tornam a colisão rara, mas não impossível, e repetir a geração é barato.
 * Um byte por caractere, módulo 32: como 256 é múltiplo de 32, a distribuição
 * é uniforme.
 */
export function gerarCodigo(bytes: Uint8Array): string {
  if (bytes.length < INVITE_LENGTH) throw new Error(`gerarCodigo precisa de ${INVITE_LENGTH} bytes`)
  let codigo = ''
  for (let i = 0; i < INVITE_LENGTH; i++) codigo += INVITE_ALPHABET[bytes[i]! % INVITE_ALPHABET.length]
  return codigo
}
